import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "../Reveal";
import { api, formatApiErrorDetail, getDevice, getUtm, getVisitorId, track } from "../../lib/api";
import { INTERETS_OPTIONS, METIERS, NB_BOX_OPTIONS, REGIONS, VEHICULES_OPTIONS } from "../../lib/constants";

const inputCls = "w-full rounded-lg border border-[#2E333D] bg-[#141619] px-4 py-3.5 text-base text-[#F9FAFB] placeholder:text-gray-600 focus:border-[#FFC700] focus:outline-none transition-colors";
const labelCls = "block font-mono text-[11px] uppercase tracking-[0.2em] text-gray-400 mb-2";

const Option = ({ active, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`px-4 py-3 rounded-lg border text-sm font-medium transition-all ${active ? "border-[#FFC700] bg-[#FFC700]/10 text-[#FFC700]" : "border-[#2E333D] bg-[#141619] text-gray-300 hover:border-gray-500"}`}>
    {children}
  </button>
);

export default function Questionnaire() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ prenom: "", email: "", metier: "", ville: "", region: "", vehicules: "", nb_box: "", commentaire: "", website: "" });
  const [interets, setInterets] = useState([]);
  const [consents, setConsents] = useState({ data: false, marketing: false });
  const [errors, setErrors] = useState({});
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const onFocus = () => {
    if (!started) {
      setStarted(true);
      track("form_start", { form: "questionnaire" });
    }
  };

  const toggleInteret = (val) =>
    setInterets((list) => (list.includes(val) ? list.filter((x) => x !== val) : [...list, val]));

  const validate = () => {
    const e = {};
    if (!form.prenom.trim()) e.prenom = "Prénom requis";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Email valide requis";
    if (!form.metier) e.metier = "Métier requis";
    if (!form.ville.trim()) e.ville = "Ville requise";
    if (!form.region) e.region = "Région requise";
    if (!form.vehicules) e.vehicules = "Réponse requise";
    if (!form.nb_box) e.nb_box = "Réponse requise";
    if (!consents.data) e.data_consent = "Consentement requis pour envoyer votre avis";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Certains champs sont incomplets. Vérifiez le formulaire.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/api/leads", {
        lead_type: "questionnaire",
        prenom: form.prenom.trim(),
        email: form.email.trim(),
        metier: form.metier,
        ville: form.ville.trim(),
        region: form.region,
        vehicules: form.vehicules,
        nb_box: form.nb_box,
        interets,
        commentaire: form.commentaire.trim() || null,
        marketing_consent: consents.marketing,
        data_consent: consents.data,
        source: "questionnaire",
        visitor_id: getVisitorId(),
        device: getDevice(),
        website: form.website,
        ...getUtm(),
      });
      track("form_submit", { form: "questionnaire" });
      toast.success("Votre avis a bien été envoyé. Merci !");
      navigate("/merci");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="questionnaire" className="py-16 sm:py-24 bg-[#141619]/40 border-y border-[#2E333D]/50" data-testid="questionnaire-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center">
          <Eyebrow>Questionnaire national</Eyebrow>
          <SectionTitle>Votre avis nous intéresse.</SectionTitle>
          <p className="mt-5 text-gray-400 leading-relaxed max-w-xl mx-auto">
            Nous développons STOCKBOX 8 avec les professionnels. Votre avis nous aidera
            à créer un produit réellement adapté à votre métier.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} onFocus={onFocus} className="mt-12 border border-[#2E333D] bg-[#0C0D0E] rounded-2xl p-6 sm:p-10 space-y-7" data-testid="questionnaire-form" noValidate>
            <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)}
              className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls} htmlFor="q-prenom">Prénom *</label>
                <input id="q-prenom" data-testid="form-prenom" className={inputCls} value={form.prenom} onChange={(e) => set("prenom", e.target.value)} placeholder="Votre prénom" />
                {errors.prenom && <p className="mt-1.5 text-xs text-red-400">{errors.prenom}</p>}
              </div>
              <div>
                <label className={labelCls} htmlFor="q-email">Email *</label>
                <input id="q-email" data-testid="form-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="vous@entreprise.fr" />
                {errors.email && <p className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls} htmlFor="q-metier">Métier *</label>
                <select id="q-metier" data-testid="form-metier" className={inputCls} value={form.metier} onChange={(e) => set("metier", e.target.value)}>
                  <option value="">Sélectionnez votre métier</option>
                  {METIERS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                {errors.metier && <p className="mt-1.5 text-xs text-red-400">{errors.metier}</p>}
              </div>
              <div>
                <label className={labelCls} htmlFor="q-vehicules">Nombre de véhicules professionnels *</label>
                <select id="q-vehicules" data-testid="form-vehicules" className={inputCls} value={form.vehicules} onChange={(e) => set("vehicules", e.target.value)}>
                  <option value="">Sélectionnez</option>
                  {VEHICULES_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
                {errors.vehicules && <p className="mt-1.5 text-xs text-red-400">{errors.vehicules}</p>}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls} htmlFor="q-ville">Ville *</label>
                <input id="q-ville" data-testid="form-ville" className={inputCls} value={form.ville} onChange={(e) => set("ville", e.target.value)} placeholder="Votre ville" />
                {errors.ville && <p className="mt-1.5 text-xs text-red-400">{errors.ville}</p>}
              </div>
              <div>
                <label className={labelCls} htmlFor="q-region">Région *</label>
                <select id="q-region" data-testid="form-region" className={inputCls} value={form.region} onChange={(e) => set("region", e.target.value)}>
                  <option value="">Sélectionnez votre région</option>
                  {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                {errors.region && <p className="mt-1.5 text-xs text-red-400">{errors.region}</p>}
              </div>
            </div>

            <div>
              <p className={labelCls}>Combien de STOCKBOX 8 pourriez-vous utiliser ? *</p>
              <div className="flex flex-wrap gap-2.5">
                {NB_BOX_OPTIONS.map((o) => (
                  <Option key={o} testid={`form-nbbox-${o.replace(/\s|\+/g, "-")}`} active={form.nb_box === o} onClick={() => set("nb_box", o)}>{o}</Option>
                ))}
              </div>
              {errors.nb_box && <p className="mt-1.5 text-xs text-red-400">{errors.nb_box}</p>}
            </div>

            <div>
              <p className={labelCls}>Qu'est-ce qui vous intéresse le plus ? (plusieurs choix possibles)</p>
              <div className="flex flex-wrap gap-2.5">
                {INTERETS_OPTIONS.map((o) => (
                  <Option key={o} testid={`form-interet-chip-${o.slice(0, 16).replace(/\s/g, "-")}`} active={interets.includes(o)} onClick={() => toggleInteret(o)}>{o}</Option>
                ))}
              </div>
            </div>

            <div>
              <label className={labelCls} htmlFor="q-commentaire">Qu'aimeriez-vous améliorer ou ajouter ?</label>
              <textarea id="q-commentaire" data-testid="form-commentaire" rows={4} className={inputCls} value={form.commentaire} onChange={(e) => set("commentaire", e.target.value)} placeholder="Vos besoins, vos remarques, les fonctionnalités que vous souhaitez..." />
            </div>

            <div className="space-y-3 border-t border-[#2E333D] pt-6">
              <label className="flex items-start gap-3 cursor-pointer text-sm text-gray-300" data-testid="form-consent-data">
                <input type="checkbox" checked={consents.data} onChange={(e) => setConsents((c) => ({ ...c, data: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 accent-[#FFC700]" />
                <span>J'accepte que mes réponses soient enregistrées et utilisées dans le cadre de l'étude de marché STOCKBOX 8. *</span>
              </label>
              {errors.data_consent && <p className="text-xs text-red-400">{errors.data_consent}</p>}
              <label className="flex items-start gap-3 cursor-pointer text-sm text-gray-400" data-testid="form-consent-marketing">
                <input type="checkbox" checked={consents.marketing} onChange={(e) => setConsents((c) => ({ ...c, marketing: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 accent-[#FFC700]" />
                <span>J'accepte de recevoir des communications marketing de STOCKBOX 8 (facultatif).</span>
              </label>
              <p className="text-xs text-gray-600">
                Vos données sont traitées conformément à notre <a href="/politique-confidentialite" className="underline hover:text-[#FFC700]">politique de confidentialité</a>. Vous pouvez demander leur suppression à tout moment.
              </p>
            </div>

            <button type="submit" disabled={submitting} data-testid="form-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#FFC700] text-[#0C0D0E] font-bold text-base px-8 py-4 rounded-full uppercase tracking-wide hover:bg-[#FFD83D] disabled:opacity-60 disabled:cursor-not-allowed transition-all">
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {submitting ? "Envoi en cours..." : "Envoyer mon avis"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
